import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../utils/api';
import { PageHeader } from '../components/layout/UI';
import { useToast, apiError } from '../components/Toast';
import { Bell, CheckSquare, ChevronLeft, ChevronRight, Search, Settings, Trash2, UserPlus } from 'lucide-react';

const SearchIc = () => <Search size={14} />;
const TrashIc = () => <Trash2 size={13} />;

const TABS = [
  { key:'all',        label:'All',         icon:Bell,           color:'#64748b' },
  { key:'assignment',  label:'Assignments', icon:UserPlus,  color:'#2E539F' },
  { key:'approval',    label:'Approvals',   icon:CheckSquare, color:'#FFC849' },
  { key:'chat',        label:'Chat',        icon:Bell,           color:'#5CC3BC' },
  { key:'system',      label:'System',      icon:Settings,  color:'#64748b' },
];

const timeAgo = (dateStr) => {
  const mins = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins/60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs/24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

export default function NotificationsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [counts,    setCounts]    = useState(null);
  const [loading,   setLoading]   = useState(true);
  const limit = 15;

  const loadCounts = useCallback(async () => {
    const r = await notificationsAPI.getCounts();
    setCounts(r.data);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await notificationsAPI.getAll({ category: activeTab, page, limit, search: search || undefined });
      setRows(r.data.rows);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [activeTab, page, search]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setPage(1); }, [activeTab, search]);

  const handleClick = async (n) => {
    if (!n.is_read) {
      // Marking read is best-effort: never block the navigation the click
      // was actually for, but do not swallow the failure either.
      try { await notificationsAPI.markRead(n.id); loadCounts(); loadRows(); }
      catch(e) { toast.error(apiError(e, 'Could not mark that as read.')); }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead(activeTab !== 'all' ? activeTab : undefined);
      toast.success('All notifications marked as read');
    } catch(e) { toast.error(apiError(e, 'Could not mark them as read.')); }
    finally { loadCounts(); loadRows(); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try { await notificationsAPI.delete(id); toast.success('Notification deleted'); }
    catch(err) { toast.error(apiError(err, 'Could not delete that notification.')); }
    finally { loadCounts(); loadRows(); }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Everything that needs your attention, in one place" />

      {/* Category tabs with counts - styled after the reference layout */}
      <div className="card mb-5 p-0 overflow-hidden">
        <div className="flex items-stretch flex-wrap border-b" style={{ borderColor:'var(--border)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const c = counts ? (tab.key==='all' ? counts.all : counts[tab.key]) : { total:0, unread:0 };
            const active = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={()=>setActiveTab(tab.key)}
                className={`flex items-center gap-2.5 px-5 py-3.5 border-b-2 transition-colors ${active ? '' : 'border-transparent'}`}
                style={active ? { borderColor: tab.color } : {}}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0" style={{ background: tab.color }}>
                  <Icon/>
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium" style={{ color: active ? 'var(--text)' : 'var(--text-secondary)' }}>{tab.label}</p>
                  <p className="text-xs font-bold" style={{ color: tab.color }}>
                    {c?.total ?? 0}{c?.unread > 0 && <span className="ml-1 text-red-500">({c.unread} new)</span>}
                  </p>
                </div>
              </button>
            );
          })}

          <div className="flex-1 flex items-center justify-end gap-2 px-4">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}><SearchIc/></span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by title or content"
                className="input pl-8 text-sm py-1.5 w-56"/>
            </div>
            <button onClick={handleMarkAllRead} className="btn-secondary text-xs whitespace-nowrap">Mark all read</button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16"><p style={{ color:'var(--text-secondary)' }}>No records found</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background:'var(--surface-alt)' }}>
                {['','Title','Detail','When',''].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(n => {
                const tabInfo = TABS.find(t => t.key === n.category) || TABS[TABS.length-1];
                return (
                  <tr key={n.id} onClick={()=>handleClick(n)}
                    className="border-b cursor-pointer hover:bg-[var(--surface-alt)] transition-colors"
                    style={{ borderColor:'var(--border)', background: n.is_read ? 'transparent' : 'rgba(46,83,159,0.04)' }}>
                    <td className="px-4 py-3 w-2">
                      {!n.is_read && <span className="w-2 h-2 rounded-full inline-block" style={{ background: tabInfo.color }}/>}
                    </td>
                    <td className="px-4 py-3">
                      <p className={`font-medium ${!n.is_read ? '' : ''}`} style={{ color:'var(--text)' }}>{n.title}</p>
                    </td>
                    <td className="px-4 py-3 max-w-md truncate" style={{ color:'var(--text-secondary)' }}>{n.body}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{timeAgo(n.created_at)}</td>
                    <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                      <button onClick={e=>handleDelete(n.id, e)} className="p-1 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><TrashIc/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor:'var(--border)' }}>
          <span className="text-xs" style={{ color:'var(--text-secondary)' }}>
            Page {page} of {totalPages} &middot; Total {total}
          </span>
          <div className="flex gap-1">
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-[var(--surface-alt)] disabled:opacity-30"><ChevronLeft size={14}/></button>
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-[var(--surface-alt)] disabled:opacity-30"><ChevronRight size={14}/></button>
          </div>
        </div>
      </div>
    </div>
  );
}
